import { IconType } from "react-icons";
import {
  FaChartBar,
  FaChartLine,
  FaCheckCircle,
  FaCoins,
  FaHandshake,
  FaMoneyBillWave,
} from "react-icons/fa";

import { Card } from "@components/cards/card";
import { IProfitTypeDTO } from "@hooks/dtos/profit/IProfitTypeDTO";

type CampaignModelsSectionProps = {
  profitTypes: IProfitTypeDTO[];
  isLoading: boolean;
};

type ModelPresentation = {
  color: "primary" | "success" | "secondary";
  icon: IconType;
  highlights: string[];
};

const modelPresentations: ModelPresentation[] = [
  {
    color: "primary",
    icon: FaCoins,
    highlights: [
      "Você fica com 100% do lucro das vendas",
      "Parceiros recebem apenas a comissão",
      "Taxas da plataforma são descontadas",
      "Ideal para quem quer máximo controle",
    ],
  },
  {
    color: "success",
    icon: FaChartBar,
    highlights: [
      "Você fica com o lucro das vendas",
      "Parceiros recebem apenas a comissão",
      "Taxas externas são descontadas",
      "Equilíbrio entre controle e escala",
    ],
  },
  {
    color: "secondary",
    icon: FaHandshake,
    highlights: [
      "Lucro dividido com a plataforma",
      "Parceiros recebem a comissão definida",
      "Taxas externas não entram na divisão",
      "Mais alcance com menos investimento",
    ],
  },
];

function getPlainText(text?: string) {
  return (text || "").replace(/<[^>]*>/g, "").trim();
}

function getPresentation(index: number): ModelPresentation {
  return modelPresentations[index] || modelPresentations[0];
}

export function CampaignModelsSection({
  profitTypes,
  isLoading,
}: CampaignModelsSectionProps) {
  return (
    <section id="modelos-campanha" className="landing-models-section py-5">
      <div className="container">
        <div className="landing-models-heading">
          <span className="badge bg-primary bg-opacity-10 rounded-pill px-3 py-2 mb-3 fs-6">
            Modelos de campanha
          </span>
          <h2 className="landing-models-title fw-bold text-dark mb-2">
            Escolha como sua campanha será estruturada
          </h2>
          <p className="landing-models-subtitle text-muted mb-0">
            Mesmos recursos, diferentes formas de distribuição do lucro. Escolha
            o modelo ideal para o seu objetivo.
          </p>
        </div>

        <div className="landing-models-grid row g-4 mt-1">
          {isLoading ? (
            [1, 2, 3].map((item) => (
              <div className="col-lg-4" key={item}>
                <Card className="landing-model-card h-100 border shadow-sm p-4">
                  <div className="placeholder-glow">
                    <span
                      className="placeholder col-2 rounded-3 mb-4"
                      style={{ height: 72 }}
                    />
                    <span className="placeholder col-5 mb-3" />
                    <span className="placeholder col-12 mb-2" />
                    <span className="placeholder col-9" />
                  </div>
                </Card>
              </div>
            ))
          ) : profitTypes.length > 0 ? (
            profitTypes.map((profit, index) => {
              const presentation = getPresentation(index);
              const Icon = presentation.icon;
              const description =
                profit.description_short || getPlainText(profit.description);

              return (
                <div className="col-lg-4" key={profit.id}>
                  <Card
                    className={`landing-model-card landing-model-card-${presentation.color} h-100 p-4 p-xl-5 shadow-sm landing-model-card-featured border border-2 border-muted`}
                  >
                    <div className="d-flex align-items-center gap-3 mb-3 mb-xl-4">
                      <div
                        className={`landing-model-icon bg-${presentation.color} rounded-2 bg-opacity-10 p-3`}
                      >
                        <Icon className="text-white fs-4" />
                      </div>
                      <h3 className="landing-model-name fw-bold text-dark mb-0">
                        {profit.name}
                      </h3>
                      <span className="badge bg-primary text-white ms-auto px-3 py-2">
                        Modelo {index + 1}
                      </span>
                    </div>

                    <p className="landing-model-description text-muted mb-4">
                      {description}
                    </p>

                    <div className="d-flex flex-column gap-2 mt-auto">
                      {presentation.highlights.map((highlight) => (
                        <div
                          className="landing-model-highlight d-flex align-items-start gap-3"
                          key={highlight}
                        >
                          <FaCheckCircle
                            className={`text-${presentation.color} flex-shrink-0`}
                          />
                          <span className="text-muted">{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              );
            })
          ) : (
            <div className="col-12">
              <div className="alert alert-warning mb-0">
                Nenhum modelo de campanha disponível no momento.
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

